import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ServiceEditPanel = ({ selectedNode, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'service',
    pricingModel: 'fixed',
    basePrice: '',
    vatRate: '20',
    warrantyMonths: '6',
    estimatedDuration: '',
    skillRequirements: [],
    seasonalPricing: false,
    geographicAvailability: 'all',
    customerValidationRequired: false,
    allowFieldAmendments: true,
    active: true
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedNode) {
      setFormData({
        name: selectedNode?.name || '',
        description: selectedNode?.description || '',
        type: selectedNode?.type || 'service',
        pricingModel: selectedNode?.pricingModel || 'fixed',
        basePrice: selectedNode?.basePrice || '',
        vatRate: selectedNode?.vatRate || '20',
        warrantyMonths: selectedNode?.warrantyMonths || '6',
        estimatedDuration: selectedNode?.estimatedDuration || '',
        skillRequirements: selectedNode?.skillRequirements || [],
        seasonalPricing: selectedNode?.seasonalPricing || false,
        geographicAvailability: selectedNode?.geographicAvailability || 'all',
        customerValidationRequired: selectedNode?.customerValidationRequired || false,
        allowFieldAmendments: selectedNode?.allowFieldAmendments !== false,
        active: selectedNode?.active !== false
      });
    }
  }, [selectedNode]);

  const typeOptions = [
    { value: 'category', label: 'Catégorie' },
    { value: 'equipment', label: 'Équipement' },
    { value: 'problem', label: 'Problème' },
    { value: 'service', label: 'Service' }
  ];

  const pricingModelOptions = [
    { value: 'fixed', label: 'Prix fixe', description: 'Tarif défini à l\'avance' },
    { value: 'quote', label: 'Sur devis', description: 'Nécessite une évaluation' }
  ];

  const vatRateOptions = [
    { value: '10', label: '10% (Particuliers)' },
    { value: '20', label: '20% (Entreprises)' }
  ];

  const skillOptions = [
    { value: 'plumbing_basic', label: 'Plomberie de base' },
    { value: 'plumbing_advanced', label: 'Plomberie avancée' },
    { value: 'heating', label: 'Chauffage' },
    { value: 'drainage', label: 'Assainissement' },
    { value: 'gas_certified', label: 'Gaz (certifié)' },
    { value: 'emergency', label: 'Interventions d\'urgence' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Le nom est obligatoire';
    }

    if (formData?.type === 'service') {
      if (formData?.pricingModel === 'fixed' && !formData?.basePrice) {
        newErrors.basePrice = 'Le prix de base est obligatoire pour les services à prix fixe';
      }
      if (!formData?.estimatedDuration) {
        newErrors.estimatedDuration = 'La durée estimée est obligatoire';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave({ ...selectedNode, ...formData });
    }
  };

  if (!selectedNode) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
          <Icon name="FileText" size={40} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Aucun élément sélectionné
        </h3>
        <p className="text-sm text-muted-foreground max-w-md">
          Sélectionnez un élément dans l'arborescence pour afficher et modifier ses détails
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="h-full flex flex-col">
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Edit" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Modifier l'élément
            </h2>
            <p className="text-sm text-muted-foreground">
              Type: {formData?.type}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            iconName="X"
            onClick={onCancel}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            iconName="Save"
          >
            Enregistrer
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="bg-card rounded-lg border border-border p-6 space-y-4">
          <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
            <Icon name="Info" size={18} />
            Informations générales
          </h3>

          <Select
            label="Type d'élément"
            options={typeOptions}
            value={formData?.type}
            onChange={(value) => handleChange('type', value)}
            required
          />

          <Input
            label="Nom"
            type="text"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            error={errors?.name}
            required
            placeholder="Ex: Réparation fuite d'eau"
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleChange('description', e?.target?.value)}
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Description détaillée du service..."
            />
          </div>

          <Checkbox
            label="Actif"
            description="L'élément est visible et disponible"
            checked={formData?.active}
            onChange={(e) => handleChange('active', e?.target?.checked)}
          />
        </div>

        {formData?.type === 'service' && (
          <>
            <div className="bg-card rounded-lg border border-border p-6 space-y-4">
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Icon name="DollarSign" size={18} />
                Tarification
              </h3>

              <Select
                label="Modèle de tarification"
                options={pricingModelOptions}
                value={formData?.pricingModel}
                onChange={(value) => handleChange('pricingModel', value)}
                required
              />

              {formData?.pricingModel === 'fixed' && (
                <>
                  <Input
                    label="Prix de base (€)"
                    type="number"
                    value={formData?.basePrice}
                    onChange={(e) => handleChange('basePrice', e?.target?.value)}
                    error={errors?.basePrice}
                    required
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />

                  <Select
                    label="Taux de TVA"
                    description="10% pour particuliers, 20% pour entreprises"
                    options={vatRateOptions}
                    value={formData?.vatRate}
                    onChange={(value) => handleChange('vatRate', value)}
                    required
                  />

                  {formData?.basePrice && (
                    <div className="p-4 bg-muted rounded-lg">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Prix HT:</span>
                        <span className="font-medium">{parseFloat(formData?.basePrice)?.toFixed(2)} €</span>
                      </div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">TVA ({formData?.vatRate}%):</span>
                        <span className="font-medium">
                          {(parseFloat(formData?.basePrice) * parseFloat(formData?.vatRate) / 100)?.toFixed(2)} €
                        </span>
                      </div>
                      <div className="flex justify-between text-base font-semibold pt-2 border-t border-border">
                        <span>Prix TTC:</span>
                        <span className="text-primary">
                          {(parseFloat(formData?.basePrice) * (1 + parseFloat(formData?.vatRate) / 100))?.toFixed(2)} €
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}

              <Checkbox
                label="Tarification saisonnière"
                description="Appliquer des prix différents selon la saison"
                checked={formData?.seasonalPricing}
                onChange={(e) => handleChange('seasonalPricing', e?.target?.checked)}
              />
            </div>

            <div className="bg-card rounded-lg border border-border p-6 space-y-4">
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Icon name="Settings" size={18} />
                Configuration du service
              </h3>

              <Input
                label="Durée estimée (minutes)"
                type="number"
                value={formData?.estimatedDuration}
                onChange={(e) => handleChange('estimatedDuration', e?.target?.value)}
                error={errors?.estimatedDuration}
                required
                placeholder="60"
                min="15"
                step="15"
              />

              <Input
                label="Garantie (mois)"
                type="number"
                value={formData?.warrantyMonths}
                onChange={(e) => handleChange('warrantyMonths', e?.target?.value)}
                placeholder="6"
                min="0"
                description="Période de garantie par défaut: 6 mois"
              />

              <Select
                label="Compétences requises"
                description="Compétences nécessaires pour ce service"
                options={skillOptions}
                value={formData?.skillRequirements}
                onChange={(value) => handleChange('skillRequirements', value)}
                multiple
                searchable
              />

              <Checkbox
                label="Autoriser les modifications terrain"
                description="Le technicien peut modifier le service sur place"
                checked={formData?.allowFieldAmendments}
                onChange={(e) => handleChange('allowFieldAmendments', e?.target?.checked)}
              />

              <Checkbox
                label="Validation client requise"
                description="Nécessite l'approbation du client pour les modifications"
                checked={formData?.customerValidationRequired}
                onChange={(e) => handleChange('customerValidationRequired', e?.target?.checked)}
              />
            </div>
          </>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
          <Icon name="AlertCircle" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">Modifications en temps réel</p>
            <p>Les changements seront immédiatement visibles dans l'assistant de réservation et les interfaces techniciens.</p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ServiceEditPanel;